import React, {useEffect, useMemo, useState, useRef} from 'react';
import {Modal, Button, Segment, Dimmer, Loader} from 'semantic-ui-react';

const defaultPosition = (n) => ({ positionNumber: n, weight: '', weightPerPlace: '', packagesCount: '', length: '', width: '', height: '' });

const PositionsModal = ({open, onClose, parcel, onSave}) => {
    const [loading, setLoading] = useState(false);
    const mountedRef = useRef(true);
    
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);
    
    const detectKey = useMemo(() => {
        if (!parcel || !parcel.data) return null;
        const keys = Object.keys(parcel.data);
        for (let k of keys) {
            const v = parcel.data[k];
            if (Array.isArray(v) && v.length >= 0) {
            const sample = v[0] || {};
            if (['weight','weightPerPlace','packagesCount','length','width','height'].some(p => p in sample)) return k;
            }
        }
        return null;
    }, [parcel]);

    const [positions, setPositions] = useState([]);

    useEffect(() => {
        if (!open || !mountedRef.current) return;
        const arr = (detectKey && parcel && parcel.data[detectKey]) ? parcel.data[detectKey] : [];
        const list = [];
        for (let i = 1; i <= 20; i++) {
            const found = (arr || []).find(p => p.positionNumber === i);
            if (found) {
                // Нормализуем значения: преобразуем в строки, обрабатываем null/undefined, сохраняем 0
                const normalized = {
                    ...defaultPosition(i),
                    positionNumber: found.positionNumber || i,
                    weight: found.weight != null ? String(found.weight) : '',
                    weightPerPlace: found.weightPerPlace != null ? String(found.weightPerPlace) : '',
                    packagesCount: found.packagesCount != null ? String(found.packagesCount) : '',
                    length: found.length != null ? String(found.length) : '',
                    width: found.width != null ? String(found.width) : '',
                    height: found.height != null ? String(found.height) : ''
                };
                list.push(normalized);
            } else {
                list.push(defaultPosition(i));
            }
        }
        if (mountedRef.current) {
            setPositions(list);
        }
    }, [open, detectKey, parcel]);

    const change = (idx, field, val) => {
        if (!mountedRef.current) return;
        setPositions(prev => prev.map((p, i) => i === idx ? { ...p, [field]: val } : p));
    };

    const save = () => {
        if (!mountedRef.current || !onSave) return;
        const clean = positions.filter(p => (p.weight || p.weightPerPlace || p.packagesCount || p.length || p.width || p.height));
        setLoading(true);
        const result = onSave(detectKey || 'positions', clean);
        // Если onSave возвращает Promise, обрабатываем его
        if (result && typeof result.then === 'function') {
            result
                .then(() => {
                    // Успешное сохранение - модалка закроется в родителе через onClose
                    if (mountedRef.current) {
                        setLoading(false);
                    }
                })
                .catch(err => {
                    console.error('Ошибка при сохранении позиций:', err);
                    if (mountedRef.current) {
                        setLoading(false);
                    }
                });
        } else {
            // Если onSave не возвращает Promise, просто выключаем загрузку
            if (mountedRef.current) {
                setLoading(false);
            }
        }
    };

    return (
        <Modal closeIcon open={open} onClose={onClose} size='large'>
            <Modal.Header>
                Детальные данные по местам — {parcel && parcel.data && parcel.data.sequentialNumber ? `№ ПП ${parcel.data.sequentialNumber}` : 'Отправление'}
            </Modal.Header>
            
            <Segment vertical style={{borderBottom: 'none'}}>
                <Dimmer active={loading} inverted>
                    <Loader size='medium'/>
                </Dimmer>
                <Modal.Content scrolling>
                    <style>{`
                        .positions-modal-table tbody tr:hover {
                            background-color: #e9f0f5 !important;
                        }
                        .positions-modal-table input:focus {
                            border-color: #459eff;
                            box-shadow: 0 0 0 0.2rem rgba(69, 158, 255, 0.25);
                        }
                    `}</style>
                    <div className="table-responsive" style={{maxHeight: '60vh'}}>
                        <table className="table table-bordered table-hover table-striped positions-modal-table" style={{minWidth: 900}}>
                            <thead style={{backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 1}}>
                                <tr>
                                    <th style={{textAlign: 'center', verticalAlign: 'middle', width: '80px', fontWeight: '600'}}>Место</th>
                                    <th style={{minWidth: '120px', fontWeight: '600'}}>Вес, кг</th>
                                    <th style={{minWidth: '120px', fontWeight: '600'}}>Вес места, кг</th>
                                    <th style={{minWidth: '120px', fontWeight: '600'}}>Кол-во мест, шт</th>
                                    <th style={{minWidth: '100px', fontWeight: '600'}}>Длина, см</th>
                                    <th style={{minWidth: '100px', fontWeight: '600'}}>Ширина, см</th>
                                    <th style={{minWidth: '100px', fontWeight: '600'}}>Высота, см</th>
                                </tr>
                            </thead>
                            <tbody>
                                {positions.map((p, idx) => (
                                    <tr key={idx} style={{backgroundColor: idx % 2 === 0 ? '#fff' : '#f8f9fa'}}>
                                        <td style={{textAlign: 'center', verticalAlign: 'middle', fontWeight: 'bold', backgroundColor: '#e9ecef'}}>
                                            {p.positionNumber}
                                        </td>
                                        <td>
                                            <input 
                                                className='form-control' 
                                                type="text"
                                                value={p.weight != null ? String(p.weight) : ''} 
                                                onChange={e => change(idx, 'weight', e.target.value)}
                                                placeholder="0.0"
                                            />
                                        </td>
                                        <td>
                                            <input 
                                                className='form-control' 
                                                type="text"
                                                value={p.weightPerPlace != null ? String(p.weightPerPlace) : ''} 
                                                onChange={e => change(idx, 'weightPerPlace', e.target.value)}
                                                placeholder="0.0"
                                            />
                                        </td>
                                        <td>
                                            <input 
                                                className='form-control' 
                                                type="text"
                                                value={p.packagesCount != null ? String(p.packagesCount) : ''} 
                                                onChange={e => change(idx, 'packagesCount', e.target.value)}
                                                placeholder="0"
                                            />
                                        </td>
                                        <td>
                                            <input 
                                                className='form-control' 
                                                type="text"
                                                value={p.length != null ? String(p.length) : ''} 
                                                onChange={e => change(idx, 'length', e.target.value)}
                                                placeholder="0"
                                            />
                                        </td>
                                        <td>
                                            <input 
                                                className='form-control' 
                                                type="text"
                                                value={p.width != null ? String(p.width) : ''} 
                                                onChange={e => change(idx, 'width', e.target.value)}
                                                placeholder="0"
                                            />
                                        </td>
                                        <td>
                                            <input 
                                                className='form-control' 
                                                type="text"
                                                value={p.height != null ? String(p.height) : ''} 
                                                onChange={e => change(idx, 'height', e.target.value)}
                                                placeholder="0"
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Modal.Content>
            </Segment>
            
            <Modal.Actions className='modal__actions'>
                <Button onClick={onClose}>
                    Отмена
                </Button>
                <Button disabled={loading} onClick={save} className='modal__actions_yes'>
                    Сохранить
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default PositionsModal;





// WEBPACK FOOTER //
// ./App/pages/massUpload/components/PositionsModal.jsx